import { useState, type ReactNode } from 'react';
import { DateTime, T, Var, useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import { useErpName } from '@/api/org/config/useErpName';
import { useArTaskStatusHistory } from '@/api/rpc/hooks/accounts-receivable/useArBillingTasks';
import {
  arTaskHistoryDisplay,
  arTaskHistoryState,
  arTaskTimelineDot,
  buildArTaskTimeline,
  isKeyTimelineStep,
  type ArTaskHistoryDisplay,
  type ArTaskTimelineDot,
  type ArTaskFieldChangeStep,
  type ArTaskStatusStep,
  type ArTaskTimeline,
  type ArTaskTimelineStep,
} from './arTaskStatusHistory';

/**
 * ERP times have no zone; Date reads them as local time, so they show as
 * the ERP recorded them.
 */
function ErpTime({ at }: { at: string }) {
  return (
    <DateTime options={{ dateStyle: 'medium', timeStyle: 'short' }}>
      {new Date(at)}
    </DateTime>
  );
}

/** Famous's status names, as the step a user would read. */
function useStatusLabel() {
  const gt = useGT();
  const context = 'AR task order timeline, a step the order reached in the ERP';
  const labels: Record<string, string> = {
    order: gt('Ordered', { context }),
    ship: gt('Shipped', { context }),
    invoice: gt('Invoiced', { context }),
    'partial receipt': gt('Partially paid', { context }),
    'paid in full': gt('Paid in full', { context }),
    deleted: gt('Deleted', { context }),
  };
  return (status: string) => labels[status.toLowerCase()] ?? status;
}

function ChangeValue({ value }: { value: string | null }) {
  if (value === null) {
    return (
      <span className="italic">
        <T context="AR task order timeline, a field that had no value">empty</T>
      </span>
    );
  }
  return <span className="text-stone-700">{value}</span>;
}

function StatusDetail({
  step,
  invoiceDate,
}: {
  step: ArTaskStatusStep;
  invoiceDate: string | null;
}) {
  if (step.status.toLowerCase() !== 'invoice' || !invoiceDate) return null;
  return (
    <span className="text-xs text-muted-foreground">
      <T context="AR task order timeline, the invoice date the ERP holds, shown under the Invoiced step">
        Invoice date <Var>{invoiceDate}</Var>
      </T>
    </span>
  );
}

function ChangeDetail({ step }: { step: ArTaskFieldChangeStep }) {
  return (
    <span className="text-xs text-muted-foreground">
      <T context="AR task order timeline, a field's old and new value in the ERP, e.g. 'DELIVERED → DLVD-SHIPPER'">
        <Var>
          <ChangeValue value={step.from} />
        </Var>{' '}
        →{' '}
        <Var>
          <ChangeValue value={step.to} />
        </Var>
      </T>
    </span>
  );
}

const DOT_CLASS: Record<ArTaskTimelineDot, string> = {
  current: 'size-2.5 bg-stone-800 ring-4 ring-stone-200',
  status: 'size-2.5 bg-stone-400',
  change: 'size-2 border border-stone-400 bg-white',
};

/** This step's dot, then the line across to the next (newer) step. */
function StepRail({
  dot,
  isLast,
}: {
  dot: ArTaskTimelineDot;
  isLast: boolean;
}) {
  return (
    <div className="flex h-2.5 items-center">
      <span
        aria-hidden
        className={cn('shrink-0 rounded-full', DOT_CLASS[dot])}
      />
      {!isLast && (
        <span aria-hidden className="ml-1.5 h-px flex-1 bg-stone-300" />
      )}
    </div>
  );
}

function StepTitle({
  step,
  isCurrent,
  statusLabel,
}: {
  step: ArTaskTimelineStep;
  isCurrent: boolean;
  statusLabel: (status: string) => string;
}) {
  return (
    <span
      className={cn(
        'text-sm',
        isCurrent ? 'font-semibold text-foreground' : 'text-stone-700',
      )}
    >
      {step.kind === 'status' ? statusLabel(step.status) : step.field}
    </span>
  );
}

/** When and by whom, on one line: "Oct 21, 2026, 9:06 AM · IAGUIRRE". */
function StepMeta({ at, by }: { at: string | null; by: string | null }) {
  if (!at && !by) return null;
  return (
    <span className="text-[11px] text-muted-foreground">
      {at && <ErpTime at={at} />}
      {at && by && ' · '}
      {by}
    </span>
  );
}

function StepDetail({
  step,
  invoiceDate,
}: {
  step: ArTaskTimelineStep;
  invoiceDate: string | null;
}) {
  return step.kind === 'status' ? (
    <StatusDetail step={step} invoiceDate={invoiceDate} />
  ) : (
    <ChangeDetail step={step} />
  );
}

function TimelineStep({
  step,
  currentKey,
  isLast,
  invoiceDate,
  statusLabel,
}: {
  step: ArTaskTimelineStep;
  currentKey: string | null;
  isLast: boolean;
  invoiceDate: string | null;
  statusLabel: (status: string) => string;
}) {
  const dot = arTaskTimelineDot(step, currentKey);
  return (
    <li className="m-0 flex w-60 shrink-0 flex-col gap-1.5">
      <StepRail dot={dot} isLast={isLast} />
      <div className="flex min-w-0 flex-col pr-3">
        <StepTitle
          step={step}
          isCurrent={dot === 'current'}
          statusLabel={statusLabel}
        />
        <StepDetail step={step} invoiceDate={invoiceDate} />
        <StepMeta at={step.at} by={step.by} />
      </div>
    </li>
  );
}

/** Shows or hides the changes outside the key fields. */
function ShowAllToggle({
  showAll,
  hiddenCount,
  onToggle,
}: {
  showAll: boolean;
  hiddenCount: number;
  onToggle: () => void;
}) {
  if (hiddenCount === 0) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="shrink-0 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
    >
      {showAll ? (
        <T context="AR task order history, hides changes outside the key fields">
          Show key changes only
        </T>
      ) : (
        <T context="AR task order history, shows the changes hidden outside the key fields">
          Show all changes (<Var>{hiddenCount}</Var> more)
        </T>
      )}
    </button>
  );
}

/**
 * Steps run oldest to newest, left to right; a long history scrolls,
 * starting at the newest step. Module-scoped so the ref callback stays the
 * same across renders.
 */
function scrollToNewest(list: HTMLOListElement | null) {
  if (list) list.scrollLeft = list.scrollWidth;
}

function OrderTimeline({
  timeline,
  shown,
}: {
  timeline: ArTaskTimeline;
  shown: readonly ArTaskTimelineStep[];
}) {
  const statusLabel = useStatusLabel();
  return (
    <ol
      ref={scrollToNewest}
      // The scroll clips what overflows, so the padding leaves room for the
      // current dot's 4px ring at the top and the first step's left edge.
      className="m-0 flex list-none overflow-x-auto px-1 pt-1 pb-1"
    >
      {shown.map((step, position) => (
        <TimelineStep
          key={step.key}
          step={step}
          currentKey={timeline.currentKey}
          isLast={position === shown.length - 1}
          invoiceDate={timeline.invoiceDate}
          statusLabel={statusLabel}
        />
      ))}
    </ol>
  );
}

const NOTICE_CLASS = 'm-0 text-xs text-muted-foreground';

/** The history's notices, naming the org's ERP (e.g. "Famous"). */
function notices(
  erpName: string,
): Record<Exclude<ArTaskHistoryDisplay, 'ready'>, ReactNode> {
  return {
    loading: (
      <T context="AR task order history is loading from the ERP">Loading…</T>
    ),
    unavailable: (
      <T context="AR task order history could not be read from the ERP; the variable is the ERP's name, e.g. Famous">
        The order history couldn't be loaded from <Var>{erpName}</Var>.
      </T>
    ),
    empty: (
      <T context="AR task order history has no changes recorded in the ERP; the variable is the ERP's name, e.g. Famous">
        No changes recorded in <Var>{erpName}</Var> yet.
      </T>
    ),
    noStatus: (
      <T context="AR task order history has other ERP changes but no status change yet; the variable is the ERP's name, e.g. Famous">
        No status changes in <Var>{erpName}</Var> yet.
      </T>
    ),
  };
}

/**
 * The order's whole history in the ERP as a horizontal timeline across the
 * top of the task, oldest to newest: its statuses (Ordered, Shipped,
 * Invoiced) and every other change, such as sale terms or credit hold, each
 * with when and by whom. The Invoiced step shows the invoice date the ERP
 * holds. Loading, an unreadable ERP and an empty history each get a short
 * line under the heading.
 */
export function ArTaskStatusTimeline({ taskId }: { taskId: string }) {
  const query = useArTaskStatusHistory(taskId);
  const erpName = useErpName();
  const state = arTaskHistoryState(query);
  const [showAll, setShowAll] = useState(false);
  const timeline = buildArTaskTimeline(
    state === 'ready' ? (query.data?.entries ?? []) : [],
    query.data?.createdAt ?? null,
  );
  const keySteps = timeline.steps.filter(isKeyTimelineStep);
  const shown = showAll ? timeline.steps : keySteps;
  const display = arTaskHistoryDisplay(state, timeline.steps, shown);
  return (
    // A tinted panel sets the history apart from the record and notes below.
    <section className="flex min-w-0 flex-col gap-2 rounded-lg bg-stone-50 px-4 py-2.5">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="m-0 text-[11px] font-semibold tracking-[0.06em] text-stone-500 uppercase">
          <T context="AR task detail, heading over the order's history timeline from the ERP">
            Order history
          </T>
        </h3>
        <ShowAllToggle
          showAll={showAll}
          hiddenCount={timeline.steps.length - keySteps.length}
          onToggle={() => setShowAll(!showAll)}
        />
      </div>
      {display === 'ready' ? (
        <OrderTimeline
          // A new list per task and per view, so its mount scrolls to the
          // newest step: switching between loaded tasks reuses the list.
          key={`${taskId}:${showAll ? 'all' : 'key'}`}
          timeline={timeline}
          shown={shown}
        />
      ) : (
        <p className={NOTICE_CLASS}>{notices(erpName)[display]}</p>
      )}
    </section>
  );
}
